import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { useApplication } from '@bonusly/application';

import { createHttpClient } from '../lib';

const RESOURCE_PATH = 'api/v2/user_content_dismissals';

interface UseUserContentDismissalProps {
	name: string;
	namespace: string;
}

interface UserContentDismissal {
	id: string;
	name: string;
	namespace: string;
	dismissed_at: string;
}

interface UserContentDismissalResponse {
	result: UserContentDismissal[] | null;
	success: boolean;
}

export const useUserContentDismissal = ({ name, namespace }: UseUserContentDismissalProps) => {
	const baseURL = useApplication.getState().application.recognizeOrigin;
	const http = createHttpClient(baseURL);
	const QUERY_KEY = ['userContentDismissals', namespace, name];

	// queryClient must be initialized by the parent component importing this hook
	const queryClient = useQueryClient();

	const { data, error, isLoading } = useQuery(
		QUERY_KEY,
		() =>
			http.get<UserContentDismissalResponse>(RESOURCE_PATH, {
				params: {
					name,
					namespace,
				},
			}),
		{
			select: (response) => response.data,
			refetchInterval: Infinity,
		}
	);

	const userContentDismissalData = data?.result?.[0] || null;
	const requestError = !isLoading && (error || !userContentDismissalData);

	const update = useMutation<void, Error, void>(
		async () => {
			await http.post(RESOURCE_PATH, {
				user_content_dismissal: {
					name,
					namespace,
				},
			});
		},
		{
			onSuccess: () => {
				queryClient.invalidateQueries(QUERY_KEY);
			},
		}
	);

	const updateUserContentDismissal = update.mutate;

	return {
		loading: isLoading,
		data: userContentDismissalData,
		error: requestError,
		updateUserContentDismissal,
	};
};
